import type { ArenaGameplayDefinition, ArenaSide, BridgeDefinition, GroundRouteState, WorldPoint } from '../types';

export interface BridgeCorridor {
  bridgeId: string;
  minimumX: number;
  maximumX: number;
}

export const arenaSideOf = (gameplay: ArenaGameplayDefinition, point: WorldPoint): ArenaSide => (
  point.y < gameplay.riverTop ? 'top' : point.y > gameplay.riverBottom ? 'bottom' : 'river'
);

export const footprintIntersectsRiver = (
  gameplay: ArenaGameplayDefinition,
  point: WorldPoint,
  radius: number,
): boolean => point.y - radius < gameplay.riverBottom && point.y + radius > gameplay.riverTop;

export const distanceToRiver = (
  gameplay: ArenaGameplayDefinition,
  point: WorldPoint,
  radius: number,
  side: Exclude<ArenaSide, 'river'>,
): number => side === 'bottom'
  ? Math.max(0, point.y - radius - gameplay.riverBottom)
  : Math.max(0, gameplay.riverTop - (point.y + radius));

export const hasClearedRiver = (
  gameplay: ArenaGameplayDefinition,
  point: WorldPoint,
  radius: number,
  sourceSide: GroundRouteState['sourceSide'],
): boolean => sourceSide === 'bottom'
  ? point.y + radius < gameplay.riverTop
  : sourceSide === 'top'
    ? point.y - radius > gameplay.riverBottom
    : false;

export const bridgeCorridor = (bridge: BridgeDefinition, radius: number): BridgeCorridor => {
  const inset = Math.min(radius, Math.max(0, bridge.width / 2 - 40));
  return {
    bridgeId: bridge.id,
    minimumX: bridge.x - bridge.width / 2 + inset,
    maximumX: bridge.x + bridge.width / 2 - inset,
  };
};

export const isAlignedWithBridge = (bridge: BridgeDefinition, point: WorldPoint, radius: number): boolean => {
  const corridor = bridgeCorridor(bridge, radius);
  return point.x >= corridor.minimumX && point.x <= corridor.maximumX;
};

export const bridgeEntryPoint = (
  gameplay: ArenaGameplayDefinition,
  bridge: BridgeDefinition,
  side: Exclude<ArenaSide, 'river'>,
  radius: number,
): WorldPoint => ({
  x: bridge.x,
  y: side === 'bottom' ? gameplay.riverBottom + radius + 50 : gameplay.riverTop - radius - 50,
});

export const bridgeExitPoint = (
  gameplay: ArenaGameplayDefinition,
  bridge: BridgeDefinition,
  sourceSide: Exclude<ArenaSide, 'river'>,
  radius: number,
): WorldPoint => ({
  x: bridge.x,
  y: sourceSide === 'bottom' ? gameplay.riverTop - radius - 50 : gameplay.riverBottom + radius + 50,
});

export const nearestBridgeByX = (
  gameplay: ArenaGameplayDefinition,
  x: number,
  targetX = x,
): BridgeDefinition => gameplay.bridges.slice().sort((left, right) => (
  Math.abs(x - left.x) - Math.abs(x - right.x)
  || Math.abs(targetX - left.x) - Math.abs(targetX - right.x)
  || left.id.localeCompare(right.id)
))[0]!;

